import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Dropzone from 'react-dropzone';
import Dropdown from 'react-bootstrap/Dropdown';

import Spinner from '../../../shared/_TBComponents/Spinner';
import SubText from '../../../shared/components/SubText/SubText';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import { getImageSource } from '../utils';
import { ColorGreen, ColorPrimary, ColorWhite } from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  setCommentMediaTarget,
  setSubActiveReduxModal,
  setUploadCommentMediaFalse,
  uploadCommentMedia,
  uploadMedia,
} from '../ducks/journalActions';

const ActionButton = styled('button')`
  align-items: center;
  background-color: ${(props) => (props.isComment ? 'rgb(240,240,240)' : ColorWhite)};
  border: ${(props) => (props.isComment ? 'none' : '1px solid #eeeeee')};
  border-radius: ${(props) => (props.isComment ? '100%' : '50%')};
  cursor: pointer;
  display: flex;
  height: ${(props) => (props.isComment ? '36px' : '56px')};
  justify-content: center;
  margin-right: ${(props) => (props.isComment ? '0' : '10px')};
  width: ${(props) => (props.isComment ? '36px' : '56px')};
  &:hover {
    background-color: #bfbfbfa1;
  }
`;

const DropDownMenuWrapper = styled('div')`
  align-items: center;
  border-radius: 20%;
  color: #333333;
  display: flex;
  justify-content: center;
  &.giphy-dropdown {
    cursor: pointer;
    height: 100%;
    position: absolute;
    right: 20px;
    top: 0px;
    z-index: 100;
  }
  &.emoji-dropdown {
    cursor: pointer;
    height: 100%;
    position: absolute;
    right: 55px;
    top: 0px;
    z-index: 100;
  }
  &.notification-dropdown {
    position: absolute;
    right: 15px;
  }
  .dropdown-container {
    border: 0px;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);
    margin-bottom: 10px;
    &.giphy {
      padding: 20px;
      z-index: 100;
    }
    &.emoji {
      padding: 0px;
      z-index: 100;
    }
  }
  .dropdown-divider {
    margin: 0px;
  }
  .dropdown-item {
    align-items: center;
    display: flex;
    padding: 15px 20px;
    transition: none;
    span {
      color: ${ColorPrimary};
    }
    .drop-icon-text {
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0px;
      margin-left: 10px;
      padding-top: 2px;
      text-transform: none;
    }
    :hover {
      background: ${ColorGreen};
      color: ${ColorWhite};
      span {
        color: ${ColorWhite};
      }
      svg {
        fill: ${ColorWhite};
      }
    }
  }
  .dropdown-menu {
    box-shadow: 0px 2px 4px 2px rgb(0 0 0 / 15%);
    padding: 10px 0px;
  }
`;

const DropZoneWrapper = styled('div')`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MediaBox = styled('div')`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const MediaImage = styled('img')`
  border-radius: 50%;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const MediaDropdownContainer = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  justify-content: center;
  .remove-button {
    background: #d6d6d6;
    border-radius: 50%;
    height: 20px;
    left: 30px;
    position: absolute;
    top: -5px;
    width: 20px;
    z-index: 100;
  }
`;

const AddMediaToggle = React.forwardRef(
  ({ children, isComment, onClick }, ref) => (
    <ActionButton
      isComment={isComment}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      ref={ref}
    >
      {children}
    </ActionButton>
  ),
);

AddMediaToggle.propTypes = {
  children: PropTypes.node,
  isComment: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

AddMediaToggle.defaultProps = {
  children: null,
  isComment: false,
};

const AttachmentButton = ({ commentId, entryId }) => {
  const commentMediaLoading = useSelector(
    (state) => state.journal.ui.commentMediaLoading,
  );
  const commentUploadMedia = useSelector(
    (state) => state.journal.data.commentUploadMedia,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isMediaLoading = useSelector(
    (state) => state.journal.ui.isMediaLoading,
  );
  const dispatch = useDispatch();

  const accountCode = currentUser?.accountCode;
  const isComment = entryId !== undefined && commentId !== undefined;

  // For comments: check if media belongs to this specific comment
  const hasMedia = isComment
    && commentUploadMedia?.entryId === entryId
    && commentUploadMedia?.commentId === commentId;

  // For comments: check if this specific comment is loading
  const mediaIsLoading = isComment
    && commentMediaLoading?.loading
    && commentMediaLoading?.entryId === entryId
    && commentMediaLoading?.commentId === commentId;

  const handleDroppedFile = (acceptedFile) => {
    const type = acceptedFile[0].type.split('/').shift();
    if (isComment) {
      dispatch(
        uploadCommentMedia(
          { accountCode, acceptedFile, type },
          entryId,
          commentId,
        ),
      );
    } else {
      dispatch(uploadMedia({ accountCode, acceptedFile, type }));
    }
    journalTracker('Journal entry', `Saved media uploaded - ${isComment ? 'Comment' : 'Entry'}`);
  };

  const handleLivePhotoClick = () => {
    const modalName = isComment
      ? 'comment-record-image-modal'
      : 'record-image-modal';
    dispatch(setSubActiveReduxModal(modalName));
    if (isComment) {
      dispatch(setCommentMediaTarget({ entryId, commentId }));
    }
    journalTracker(`${isComment ? 'Journal entry comment' : 'Journal entry'}`, 'Record live image modal opened');
  };

  const handleLiveVideoClick = () => {
    const modalName = isComment
      ? 'comment-record-video-modal'
      : 'record-video-modal';
    dispatch(setSubActiveReduxModal(modalName));
    if (isComment) {
      dispatch(setCommentMediaTarget({ entryId, commentId }));
    }
    journalTracker(`${isComment ? 'Journal entry comment' : 'Journal entry'}`, 'Record live video modal opened');
  };

  const handleRemoveMedia = () => {
    if (isComment) {
      dispatch(setUploadCommentMediaFalse());
    }
  };

  const renderMediaIcon = (mediaObj) => (
    <MediaBox>
      {mediaObj?.mediaUrl && (
        <MediaImage src={getImageSource(mediaObj)} alt='Attachment preview' />
      )}
    </MediaBox>
  );

  const renderToggleContent = () => {
    if (isComment) {
      if (mediaIsLoading) {
        return <Spinner large={false} />;
      }
      if (hasMedia && commentUploadMedia?.media?.mediaUrl) {
        return renderMediaIcon(commentUploadMedia.media);
      }
      return (
        <IcomoonReact
          iconSet={iconSet}
          size={20}
          icon='attachment'
          color='grey'
        />
      );
    }
    return (
      <IcomoonReact
        iconSet={iconSet}
        size={24}
        icon='attachment'
        color='grey'
      />
    );
  };

  const renderFileAccepter = () => (
    <Dropzone
      accept='image/*,video/*'
      onDrop={(acceptedFile) => handleDroppedFile(acceptedFile)}
    >
      {({ getRootProps, getInputProps }) => (
        <DropZoneWrapper {...getRootProps()}>
          <input {...getInputProps()} />
          <Dropdown.Item aria-label='Add saved media'>
            <IcomoonReact iconSet={iconSet} size={20} icon='attachment' />
            <SubText className='drop-icon-text' fontSize={14}>
              Add Saved Media
            </SubText>
          </Dropdown.Item>
        </DropZoneWrapper>
      )}
    </Dropzone>
  );

  const renderDropdown = () => (
    <DropDownMenuWrapper>
      <Dropdown bsPrefix='trick-fix'>
        <Dropdown.Toggle as={AddMediaToggle} isComment={isComment} aria-label='Attach media'>
          {renderToggleContent()}
        </Dropdown.Toggle>
        <Dropdown.Menu className='dropdown-container emoji'>
          <Dropdown.Item
            className='dropdown-item'
            onClick={handleLivePhotoClick}
            aria-label='Add live photo'
          >
            <IcomoonReact iconSet={iconSet} size={20} icon='camera' />
            <SubText className='drop-icon-text' fontSize={14}>
              Add Live Photo
            </SubText>
          </Dropdown.Item>
          <Dropdown.Item onClick={handleLiveVideoClick} aria-label='Add live video'>
            <IcomoonReact iconSet={iconSet} size={20} icon='video' />
            <SubText className='drop-icon-text' fontSize={14}>
              Add Live Video
            </SubText>
          </Dropdown.Item>
          {renderFileAccepter()}
          {isComment && hasMedia && commentUploadMedia?.media?.mediaUrl && (
            <Dropdown.Item onClick={handleRemoveMedia} aria-label='Remove media'>
              <IcomoonReact iconSet={iconSet} size={20} icon='trashcan' />
              <SubText className='drop-icon-text' fontSize={14}>
                Remove Media
              </SubText>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  );

  const renderContent = () => {
    if (isMediaLoading && !isComment) return null;
    return renderDropdown();
  };

  return <MediaDropdownContainer>{renderContent()}</MediaDropdownContainer>;
};

AttachmentButton.propTypes = {
  commentId: PropTypes.number,
  entryId: PropTypes.number,
};

AttachmentButton.defaultProps = {
  commentId: undefined,
  entryId: undefined,
};

export default AttachmentButton;
